import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Typography, 
  Box, 
  CircularProgress, 
  Alert, 
  AlertTitle, 
  Button, 
  Paper, 
  IconButton, 
  Divider,
  Stack,
  Pagination,
  PaginationItem,
  CssBaseline,
  TextField,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import DataGrid from './components/DataGrid';
import ThemeToggle from './components/ThemeToggle';
import { useTheme } from './contexts/ThemeContext';
import { lightTheme, darkTheme } from './theme';
import { 
  fetchImages, 
  fetchLoraModels, 
  createLoraModel, 
  updateImageRecords, 
  fetchImageCount,
  fetchSalons,
  fetchDesigners,
  fetchHairStyles,
  fetchHairLengths,
  fetchBangs,
  fetchHairColors
} from './services/api';
import { GENDERS, IMAGE_SOURCES } from './types';
import './App.css';
import { motion, AnimatePresence } from 'framer-motion';

function App() {
  const { theme } = useTheme();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [newLoraModel, setNewLoraModel] = useState('');
  const [selectedLoraModel, setSelectedLoraModel] = useState('');
  const [loraModels, setLoraModels] = useState([]);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const [loadingModels, setLoadingModels] = useState(false);
  const [savingLora, setSavingLora] = useState(false);
  const [selectedImageUrlMap, setSelectedImageUrlMap] = useState({});
  
  // 페이지네이션 관련 상태
  const [totalImages, setTotalImages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(100); // 페이지당 이미지 수
  const [totalPages, setTotalPages] = useState(1);
  const [pageRangeStart, setPageRangeStart] = useState(1); // 페이지 범위 시작점
  const [slideDirection, setSlideDirection] = useState('right'); // 슬라이드 방향 ('left' 또는 'right')
  
  // 필터 옵션 데이터를 저장할 상태 추가
  const [filterOptions, setFilterOptions] = useState({
    image_source_list: Object.values(IMAGE_SOURCES),
    gender_list: Object.values(GENDERS),
    usable_list: [true, false],
    salon_id_list: [],
    designer_id_list: [],
    hair_style_id_list: [],
    hair_length_id_list: [],
    bangs_id_list: [],
    hair_color_id_list: [],
    lora_model_id_list: [],
  });
  
  const dataGridRef = useRef(null);  // DataGrid ref 추가
  
  // 로컬 스토리지에서 상태 복원
  useEffect(() => {
    const savedFilters = localStorage.getItem('filters');
    const savedSelectedRecords = localStorage.getItem('selectedRecords');
    const savedSelectedLoraModel = localStorage.getItem('selectedLoraModel');
    const savedCurrentPage = localStorage.getItem('currentPage');
    const savedSelectedImageUrlMap = localStorage.getItem('selectedImageUrlMap');
    
    if (savedFilters) {
      setFilters(JSON.parse(savedFilters));
    }
    
    if (savedSelectedRecords) {
      setSelectedRecords(JSON.parse(savedSelectedRecords));
    }
    
    if (savedSelectedLoraModel) {
      setSelectedLoraModel(JSON.parse(savedSelectedLoraModel));
    }

    // 저장된 현재 페이지 값이 있으면 복원
    if (savedCurrentPage) {
      setCurrentPage(parseInt(savedCurrentPage, 10));
    }

    if (savedSelectedImageUrlMap) {
      setSelectedImageUrlMap(JSON.parse(savedSelectedImageUrlMap));
    }
  }, []);
  
  // 상태 변경 시 로컬 스토리지에 저장
  useEffect(() => {
    localStorage.setItem('filters', JSON.stringify(filters));
  }, [filters]);
  
  useEffect(() => {
    localStorage.setItem('selectedRecords', JSON.stringify(selectedRecords));
  }, [selectedRecords]);
  
  useEffect(() => {
    localStorage.setItem('selectedLoraModel', JSON.stringify(selectedLoraModel));
  }, [selectedLoraModel]);

  useEffect(() => {
    localStorage.setItem('selectedImageUrlMap', JSON.stringify(selectedImageUrlMap));
  }, [selectedImageUrlMap]);

  // 현재 페이지 변경 시 로컬 스토리지에 저장
  useEffect(() => {
    localStorage.setItem('currentPage', currentPage.toString());
  }, [currentPage]);

  const fetchData = useCallback(async (page = currentPage, newFilters = filters) => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching with filters:', newFilters, 'page:', page);  // 요청 시 필터와 페이지 로그
      const data = await fetchImages(newFilters, page, limit);
      console.log('Received new image data:', data);  // 받은 데이터 로그
      
      // API에서 받은 lora_model_ids를 lora_model_id_list로 변환
      const processedData = data.map(image => {
        // lora_model_ids가 있으면 처리
        if (image.lora_model_ids && Array.isArray(image.lora_model_ids)) {
          // 현재 loraModels 상태를 직접 참조하지 않고 함수 형태로 사용
          const loraModelsList = image.lora_model_ids.map(id => {
            // 현재 loraModels 상태에서 해당 ID에 맞는 모델 정보 찾기
            const model = loraModels.find(m => m.id === id);
            return model ? { id, name: model.name } : { id, name: `모델 ${id}` };
          });
          
          // 원본 데이터 유지하면서 lora_model_id_list 추가
          return {
            ...image,
            lora_model_id_list: loraModelsList
          };
        }
        
        // lora_model_ids가 없으면 빈 배열로 초기화
        return {
          ...image,
          lora_model_id_list: []
        };
      });
      
      setImages(processedData);
      console.log('Updated images state with processed data');  // 상태 업데이트 로그
    } catch (err) {
      let errorMessage = '데이터를 불러오는 중 오류가 발생했습니다.';
      
      // 에러 세부 정보 추가
      if (err.response) {
        // 서버 응답이 있는 경우
        errorMessage += ` 상태 코드: ${err.response.status}`;
        if (err.response.data && err.response.data.detail) {
          errorMessage += `, 세부 정보: ${err.response.data.detail}`;
        }
      } else if (err.request) {
        // 요청은 보냈지만 응답이 없는 경우
        errorMessage += ' 서버로부터 응답이 없습니다.';
      } else {
        // 요청 설정 중 오류가 발생한 경우
        errorMessage += ` 오류 메시지: ${err.message}`;
      }
      
      setError(errorMessage);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, limit, loraModels]); // loraModels 의존성 추가

  // 이미지 총 개수 가져오기
  const fetchTotalImageCount = useCallback(async (newFilters = filters) => {
    // 필터가 없는 경우 불필요한 API 호출 방지
    if (Object.keys(newFilters).length === 0 && totalImages > 0) {
      console.log('이미 이미지 총 개수가 로드되어 있고 필터가 없습니다. 다시 로드하지 않습니다.');
      return totalImages;
    }
    
    try {
      console.log('이미지 총 개수 요청 - 필터:', newFilters);
      const count = await fetchImageCount(newFilters);
      console.log('이미지 총 개수 응답:', count);
      setTotalImages(count);
      setTotalPages(Math.ceil(count / limit));
      return count;
    } catch (err) {
      console.error('이미지 개수를 불러오는 중 오류가 발생했습니다:', err);
      return 0;
    }
  }, [filters, limit, totalImages]);

  // LoRA 모델 목록 가져오기
  const fetchLoraModelData = useCallback(async () => {
    // 이미 로드된 모델이 있으면 다시 로드하지 않음
    if (loraModels.length > 0) {
      console.log('이미 로드된 LoRA 모델이 있습니다. 다시 로드하지 않습니다.');
      return loraModels;
    }
    
    console.log('LoRA 모델 데이터 로드 시작');
    setLoadingModels(true);
    try {
      const models = await fetchLoraModels();
      console.log(`${models.length}개의 LoRA 모델을 로드했습니다.`);
      setLoraModels(models);
      
      // filterOptions의 lora_model_id_list도 업데이트
      setFilterOptions(prev => ({
        ...prev,
        lora_model_id_list: models.map(model => ({
          id: model.id,
          name: model.name
        }))
      }));
      
      return models; // 모델 데이터 반환
    } catch (err) {
      console.error('LoRA 모델을 불러오는 중 오류가 발생했습니다:', err);
      return []; // 오류 시 빈 배열 반환
    } finally {
      setLoadingModels(false);
    }
  }, [loraModels]);
  
  // 필터 옵션 데이터를 한 번만 로드하는 useEffect 추가
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        console.log('필터 옵션 데이터 로드 시작');
        
        // 미용실 목록 로드
        const salons = await fetchSalons();
        setFilterOptions(prev => ({
          ...prev,
          salon_id_list: salons.map(salon => ({
            id: salon.id,
            name: salon.name
          }))
        }));
        
        // 디자이너 목록 로드
        const designers = await fetchDesigners();
        setFilterOptions(prev => ({
          ...prev,
          designer_id_list: designers.map(designer => ({
            id: designer.id,
            name: designer.name
          }))
        }));
        
        // 헤어 스타일 목록 로드
        const hairStyles = await fetchHairStyles();
        setFilterOptions(prev => ({
          ...prev,
          hair_style_id_list: hairStyles.map(style => ({
            id: style.id,
            name: style.name
          }))
        }));
        
        // 헤어 길이 목록 로드
        const hairLengths = await fetchHairLengths();
        setFilterOptions(prev => ({
          ...prev,
          hair_length_id_list: hairLengths.map(length => ({
            id: length.id,
            name: length.name
          }))
        }));
        
        // 앞머리 목록 로드
        const bangs = await fetchBangs();
        setFilterOptions(prev => ({
          ...prev,
          bangs_id_list: bangs.map(bang => ({
            id: bang.id,
            name: bang.name
          }))
        }));
        
        // 헤어 색상 목록 로드
        const hairColors = await fetchHairColors();
        setFilterOptions(prev => ({
          ...prev,
          hair_color_id_list: hairColors.map(color => ({
            id: color.id,
            name: color.name
          }))
        }));
        
        console.log('필터 옵션 데이터 로드 완료');
      } catch (error) {
        console.error('필터 옵션 데이터 로드 중 오류 발생:', error);
      }
    };
    
    loadFilterOptions();
  }, []); // 빈 의존성 배열로 앱 시작 시 한 번만 실행

  // 초기 데이터 로딩
  useEffect(() => {
    const loadInitialData = async () => {
      console.log('초기 데이터 로딩 시작');
      
      // 1. 먼저 LoRA 모델 데이터 로드
      await fetchLoraModelData();
      
      // 2. 그 다음 이미지 데이터와 카운트 로드
      // 저장된 페이지 번호를 사용하여 현재 페이지 유지
      console.log(`초기 이미지 데이터 로드 (페이지: ${currentPage})`);
      fetchData(currentPage, filters);
      
      // 3. 이미지 총 개수 로드 (필터가 있는 경우에만)
      if (Object.keys(filters).length > 0) {
        console.log('필터가 적용된 상태에서 이미지 총 개수 로드');
        fetchTotalImageCount(filters);
      } else {
        console.log('필터 없이 이미지 총 개수 로드');
        fetchTotalImageCount({});
      }
    };
    
    loadInitialData();
  }, [fetchLoraModelData, fetchData, fetchTotalImageCount]); // filters와 currentPage 제거

  const handleFilterChange = (filterType, value) => {
    console.log('필터 변경:', filterType, value);
    const newFilters = {
      ...filters,
      [filterType]: value,
    };
    console.log('새로운 필터:', newFilters);
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    if (dataGridRef.current) {
      dataGridRef.current.handleApplyFilters();
    }
  };

  const handleCellChange = (rowId, columnId, value) => {
    // 로컬 상태 업데이트
    setImages((prevImages) =>
      prevImages.map((image) =>
        image.id === rowId ? { ...image, [columnId]: value } : image
      )
    );
  };
  
  // 선택된 이미지들을 일괄 업데이트하는 함수
  const handleBatchUpdate = async (columnId, value) => {
    if (selectedRecords.length === 0) {
      alert('업데이트할 이미지를 선택해주세요.');
      return;
    }
    
    try {
      // 현재 loraModels 상태의 스냅샷 가져오기
      const currentLoraModels = [...loraModels];
      
      // 필드 이름 매핑 (API 요청 형식에 맞게)
      const fieldMapping = {
        'salon_id': 'salon_id',
        'designer_id': 'designer_id',
        'hair_style_id': 'hair_style_id',
        'hair_length_id': 'hair_length_id',
        'bangs_id': 'bangs_id',
        'hair_color_id': 'hair_color_id',
        'gender': 'gender',
        'usable': 'usable',
        'caption': 'caption',
        'image_source': 'image_source',
        'lora_model_id_list': 'lora_model_ids'
      };
      
      // lora_model_id_list 특별 처리 (ID 배열로 변환)
      let processedValue = value;
      if (columnId === 'lora_model_id_list' && Array.isArray(value)) {
        processedValue = value.map(model => typeof model === 'object' ? model.id : model);
      }
      
      // 선택된 각 이미지에 대한 업데이트 데이터 생성
      const imageUpdates = selectedRecords.map(imageId => ({
        id: imageId,
        [fieldMapping[columnId] || columnId]: processedValue
      }));
      
      // API를 통해 서버에 일괄 업데이트 요청
      await updateImageRecords(imageUpdates);
      
      // 성공 시 현재 페이지의 이미지만 로컬 상태 업데이트
      setImages((prevImages) => {
        return prevImages.map((image) => {
          if (selectedRecords.includes(image.id)) {
            // lora_model_id_list인 경우 특별 처리
            if (columnId === 'lora_model_id_list') {
              // UI 표시용 lora_model_id_list 생성
              const updatedLoraModelList = Array.isArray(value) ? 
                value.map(modelId => {
                  const modelInfo = typeof modelId === 'object' ? 
                    modelId : 
                    currentLoraModels.find(m => m.id === modelId);
                  
                  return typeof modelId === 'object' ? 
                    modelId : 
                    { 
                      id: modelId, 
                      name: modelInfo ? modelInfo.name : `모델 ${modelId}` 
                    };
                }) : 
                [];
              
              return { 
                ...image, 
                lora_model_ids: processedValue,
                lora_model_id_list: updatedLoraModelList
              };
            }
            
            // 다른 필드는 그대로 업데이트
            return { ...image, [columnId]: value };
          }
          return image;
        });
      });
      
      alert(`${selectedRecords.length}개의 이미지가 성공적으로 업데이트되었습니다.`);
    } catch (error) {
      console.error('이미지 일괄 업데이트 중 오류가 발생했습니다:', error);
      alert('이미지 일괄 업데이트 중 오류가 발생했습니다.');
    }
  };
  
  const handleRecordSelection = (recordId, selected) => {
    if (selected) {
      setSelectedRecords(prev => [...prev, recordId]);
      
      // 선택된 이미지의 URL을 맵에 추가
      const selectedImage = images.find(img => img.id === recordId);
      if (selectedImage && selectedImage.image_url) {
        setSelectedImageUrlMap(prev => ({
          ...prev,
          [recordId]: selectedImage.image_url
        }));
      }
    } else {
      setSelectedRecords(prev => prev.filter(id => id !== recordId));
      
      // 선택 해제된 이미지의 URL을 맵에서 제거
      setSelectedImageUrlMap(prev => {
        const newMap = { ...prev };
        delete newMap[recordId];
        return newMap;
      });
    }
  };
  
  const handleImagePreview = (imageUrl) => {
    setSelectedImageUrl(imageUrl);
  };
  
  const handleCreateLoraModel = async () => {
    if (!newLoraModel.trim()) return;
    
    try {
      const createdModel = await createLoraModel(newLoraModel);
      setLoraModels(prev => [...prev, createdModel]);
      setNewLoraModel('');
      // 새로 생성된 모델을 선택
      setSelectedLoraModel(createdModel.id);
    } catch (err) {
      console.error('LoRA 모델 생성 중 오류가 발생했습니다:', err);
      alert('LoRA 모델 생성 중 오류가 발생했습니다.');
    }
  };
  
  const handleSaveLoraToSelectedRecords = async () => {
    if (!selectedLoraModel || selectedRecords.length === 0) return;
    
    setSavingLora(true);
    try {
      // 현재 loraModels 상태의 스냅샷 가져오기
      const currentLoraModels = [...loraModels];
      
      // 모든 선택된 이미지 ID에 대해 업데이트 데이터 생성
      const imageUpdates = selectedRecords.map(imageId => ({
        id: imageId,
        lora_model_ids: [selectedLoraModel] // 새 LoRA 모델 ID 추가
      }));
      
      // API를 통해 서버에 일괄 업데이트 요청
      await updateImageRecords(imageUpdates);
      
      // 현재 페이지의 이미지 데이터 업데이트
      setImages(prevImages => 
        prevImages.map(image => {
          if (selectedRecords.includes(image.id)) {
            // 현재 이미지의 LoRA 모델 ID 목록 가져오기
            const currentLoraModelIds = image.lora_model_ids || [];
            
            // 선택된 LoRA 모델이 이미 목록에 있는지 확인
            if (!currentLoraModelIds.includes(selectedLoraModel)) {
              // 없으면 추가
              const updatedLoraModelIds = [...currentLoraModelIds, selectedLoraModel];
              
              // lora_model_id_list도 함께 업데이트 (UI 표시용)
              const selectedModelInfo = currentLoraModels.find(m => m.id === selectedLoraModel);
              const updatedLoraModelList = [
                ...(image.lora_model_id_list || []),
                { 
                  id: selectedLoraModel, 
                  name: selectedModelInfo ? selectedModelInfo.name : `모델 ${selectedLoraModel}` 
                }
              ];
              
              return {
                ...image,
                lora_model_ids: updatedLoraModelIds,
                lora_model_id_list: updatedLoraModelList
              };
            }
          }
          return image;
        })
      );
      
      // 성공 메시지
      alert(`${selectedRecords.length}개 이미지에 LoRA 모델이 적용되었습니다.`);
    } catch (error) {
      console.error('LoRA 모델 적용 중 오류가 발생했습니다:', error);
      alert('LoRA 모델 적용 중 오류가 발생했습니다.');
    } finally {
      setSavingLora(false);
    }
  };
  
  const handleCopySelectedUrls = () => {
    // 선택된 모든 이미지 URL을 맵에서 가져옴
    const urls = Object.values(selectedImageUrlMap).join('\n');
    
    if (urls) {
      navigator.clipboard.writeText(urls)
        .then(() => {
          alert('URL이 클립보드에 복사되었습니다.');
        })
        .catch(err => {
          console.error('클립보드 복사 중 오류가 발생했습니다:', err);
          alert('클립보드 복사 중 오류가 발생했습니다.');
        });
    }
  };

  // 선택된 이미지 URL 목록 생성 - 맵에서 URL 가져오기
  const selectedImageUrls = Object.values(selectedImageUrlMap).join('\n');

  // 선택된 이미지 URL 표시를 위한 스타일 추가
  const urlDisplayStyle = {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    fontFamily: 'monospace'
  };

  // 페이지 변경 처리
  const handlePageChange = (event, page) => {
    console.log(`페이지 변경: ${currentPage} -> ${page}`);
    setCurrentPage(page);
    
    // 페이지 변경 시 이미지 데이터만 다시 로드 (count나 lora-models는 로드하지 않음)
    fetchData(page);
  };

  // 페이지 범위 이동 (실제 페이지 이동 없이 보여지는 페이지 번호만 변경)
  const handlePageRangeChange = (direction) => {
    if (direction === 'prev') {
      // 이전 10개 페이지 범위로 이동
      const newStart = Math.max(1, pageRangeStart - 10);
      setSlideDirection('right'); // 오른쪽에서 왼쪽으로 슬라이드
      setPageRangeStart(newStart);
    } else if (direction === 'next') {
      // 다음 10개 페이지 범위로 이동
      const newStart = Math.min(totalPages - 9, pageRangeStart + 10);
      // 마지막 페이지가 10개 미만으로 남았을 경우 조정
      setSlideDirection('left'); // 왼쪽에서 오른쪽으로 슬라이드
      setPageRangeStart(newStart > 0 ? newStart : pageRangeStart);
    }
  };

  // 현재 페이지가 변경되면 페이지 범위도 자동으로 조정
  useEffect(() => {
    // 현재 페이지가 보이는 범위를 벗어났는지 확인
    if (currentPage < pageRangeStart || currentPage > pageRangeStart + 9) {
      // 현재 페이지가 속한 범위의 시작점 계산 (1-10, 11-20, 21-30, ...)
      const newStart = Math.floor((currentPage - 1) / 10) * 10 + 1;
      setPageRangeStart(newStart);
    }
  }, [currentPage]);

  // 총 페이지 수가 변경되면 페이지 범위도 조정
  useEffect(() => {
    // 현재 페이지 범위가 총 페이지 수를 초과하는지 확인
    if (pageRangeStart > totalPages) {
      // 마지막 페이지가 속한 범위의 시작점 계산
      const newStart = Math.max(1, Math.floor((totalPages - 1) / 10) * 10 + 1);
      setPageRangeStart(newStart);
    }
  }, [totalPages]);

  // 선택 기록 및 캐시 삭제 함수 추가
  const handleClearCache = () => {
    // 로컬 스토리지에서 캐시된 데이터 삭제
    localStorage.removeItem('filters');
    localStorage.removeItem('selectedRecords');
    localStorage.removeItem('selectedLoraModel');
    localStorage.removeItem('currentPage');
    localStorage.removeItem('selectedImageUrlMap');
    
    // 상태 초기화
    setSelectedRecords([]);
    setSelectedImageUrlMap({});
    setSelectedImageUrl('');
    setFilters({});
    setCurrentPage(1);
    
    // DataGrid 필터 초기화
    if (dataGridRef.current) {
      dataGridRef.current.clearFilters();
    }
    
    // 데이터 다시 로드 (필터 없이)
    fetchData(1, {});
    fetchTotalImageCount({});
    
    // 알림 표시
    alert('선택 기록과 캐시가 모두 삭제되었습니다.');
  };

  return (
    <MuiThemeProvider theme={theme === 'light' ? lightTheme : darkTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Box sx={{ 
          p: 2, 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column',
          position: 'relative' 
        }}>
          {/* 상단 도구 모음 */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 3,
            gap: 2,
            flexWrap: { xs: 'wrap', md: 'nowrap' }
          }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
              <TextField
                size="small"
                placeholder="새 LoRA 모델 이름"
                value={newLoraModel}
                onChange={(e) => setNewLoraModel(e.target.value)}
              />
              <Button
                variant="contained"
                onClick={handleCreateLoraModel}
                disabled={!newLoraModel.trim()}
              >
                LoRA 생성
              </Button>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>LoRA 모델 선택</InputLabel>
                <Select
                  value={selectedLoraModel}
                  onChange={(e) => setSelectedLoraModel(e.target.value)}
                  label="LoRA 모델 선택"
                >
                  <MenuItem value="">
                    <em>선택 안함</em>
                  </MenuItem>
                  {loraModels.map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      {model.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                onClick={handleApplyFilters}
              >
                필터 적용
              </Button>
            </Box>
            <ThemeToggle />
          </Box>

          <Box sx={{ 
            display: 'flex', 
            width: '100%', 
            gap: 1,
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            flexGrow: 1
          }}>
            <Box sx={{ 
              width: { xs: '100%', md: '450px' },
              flexShrink: 0
            }}>
              <Stack spacing={2}>
                <Paper sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6">
                      선택된 이미지 URL ({selectedRecords.length})
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton 
                        onClick={handleCopySelectedUrls}
                        disabled={selectedRecords.length === 0}
                        size="small"
                        title="URL 복사"
                      >
                        <ContentCopyIcon />
                      </IconButton>
                      <Button 
                        variant="contained" 
                        size="small"
                        onClick={handleSaveLoraToSelectedRecords}
                        disabled={!selectedLoraModel || selectedRecords.length === 0 || savingLora}
                      >
                        {savingLora ? <CircularProgress size={24} /> : '저장'}
                      </Button>
                      <Button 
                        variant="outlined" 
                        color="error" 
                        size="small"
                        onClick={handleClearCache}
                        title="선택 기록 및 캐시 삭제"
                        startIcon={<ClearAllIcon />}
                      >
                        초기화
                      </Button>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 1 }} />
                  <Box 
                    sx={{ 
                      flexGrow: 1, 
                      overflow: 'auto', 
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      bgcolor: 'background.paper',
                      p: 1,
                      borderRadius: 1,
                      minHeight: '190px',
                      maxHeight: '230px'
                    }}
                  >
                    {selectedImageUrls || '선택된 이미지가 없습니다.'}
                  </Box>
                </Paper>
                
                <Paper sx={{ p: 2, height: '450px' }}>
                  <Typography variant="h6" gutterBottom>
                    이미지 미리보기
                  </Typography>
                  <Box 
                    sx={{ 
                      width: '100%', 
                      height: '380px', 
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      overflow: 'hidden'
                    }}
                  >
                    {selectedImageUrl ? (
                      <img 
                        src={selectedImageUrl} 
                        alt="선택된 이미지" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '100%', 
                          objectFit: 'contain' 
                        }} 
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300x300?text=이미지+로드+실패';
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        이미지를 선택하세요
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Stack>
            </Box>
            
            <Box sx={{ 
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              ml: { xs: 0, md: 1 }
            }}>
              <Box sx={{ 
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100vh - 120px)', // 상단 여백만 고려하고 하단 여백은 최소화
                position: 'relative' // 상대적 위치 설정
              }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : error ? (
                  <Alert severity="error">
                    <AlertTitle>오류</AlertTitle>
                    {error}
                  </Alert>
                ) : (
                  <>
                    <Box sx={{ 
                      width: '100%', 
                      height: '100%', // 전체 높이 사용
                      overflow: 'hidden'  // 내부 스크롤을 위해 overflow 설정
                    }}>
                      <DataGrid 
                        ref={dataGridRef}
                        data={images} 
                        onCellChange={handleCellChange} 
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onRecordSelection={handleRecordSelection}
                        selectedRecords={selectedRecords}
                        onImagePreview={handleImagePreview}
                        filterOptions={filterOptions}
                        getTempFilters={async (tempFilters) => {
                          console.log('필터 적용 시작:', tempFilters);
                          
                          // 1. 먼저 필터 상태 업데이트
                          setFilters(tempFilters);
                          setCurrentPage(1);
                          
                          try {
                            // 2. 필터에 해당하는 전체 이미지 수 먼저 가져오기
                            console.log('이미지 카운트 요청 시작 - 필터:', tempFilters);
                            const count = await fetchImageCount(tempFilters);
                            console.log('이미지 카운트 응답:', count);
                            setTotalImages(count);
                            const newTotalPages = Math.ceil(count / limit);
                            console.log('새로운 총 페이지 수:', newTotalPages);
                            setTotalPages(newTotalPages);
                            
                            // 3. 이미지 리스트 가져오기 (페이지는 항상 1로 리셋)
                            console.log('이미지 리스트 요청 시작');
                            await fetchData(1, tempFilters);
                            console.log('이미지 리스트 요청 완료');
                          } catch (error) {
                            console.error('필터 적용 중 오류 발생:', error);
                            setError('필터 적용 중 오류가 발생했습니다: ' + error.message);
                          }
                        }}
                      />
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center', 
                      py: 0.5,
                      position: 'absolute', // 절대 위치로 변경
                      bottom: 0, // 하단에 고정
                      left: 0,
                      right: 0,
                      borderTop: 1,
                      borderColor: 'divider',
                      bgcolor: 'background.paper', // theme.palette.background.paper 대신 MUI 시스템 속성 사용
                      zIndex: 10 // 다른 요소 위에 표시
                    }}>
                      <Typography variant="caption" sx={{ ml: 1 }}>
                        총 {totalImages}개 이미지 중 {images.length > 0 ? (currentPage - 1) * limit + 1 : 0}-{Math.min(currentPage * limit, totalImages)}
                      </Typography>
                      
                      {/* 커스텀 페이지네이션 */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Button
                          size="small"
                          onClick={() => handlePageRangeChange('prev')}
                          disabled={pageRangeStart <= 1}
                          sx={{ minWidth: '30px', p: 0.5 }}
                        >
                          {'<'}
                        </Button>
                        
                        <Box sx={{ display: 'flex', overflow: 'hidden', width: 'auto', mx: 1 }}>
                          <AnimatePresence mode="wait">
                            <motion.div
                              key={pageRangeStart}
                              initial={{ x: slideDirection === 'left' ? 50 : -50, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              exit={{ x: slideDirection === 'left' ? -50 : 50, opacity: 0 }}
                              transition={{ duration: 0.3 }}
                              style={{ display: 'flex' }}
                            >
                              {Array.from({ length: Math.min(10, totalPages - pageRangeStart + 1) }, (_, i) => {
                                const page = pageRangeStart + i;
                                return (
                                  <Button
                                    key={page}
                                    size="small"
                                    variant={currentPage === page ? "contained" : "text"}
                                    color="primary"
                                    onClick={() => handlePageChange(null, page)}
                                    sx={{ 
                                      minWidth: '30px', 
                                      height: '30px',
                                      mx: 0.2,
                                      p: 0.5,
                                      lineHeight: 1
                                    }}
                                  >
                                    {page}
                                  </Button>
                                );
                              })}
                            </motion.div>
                          </AnimatePresence>
                        </Box>
                        
                        <Button
                          size="small"
                          onClick={() => handlePageRangeChange('next')}
                          disabled={pageRangeStart + 9 >= totalPages}
                          sx={{ minWidth: '30px', p: 0.5 }}
                        >
                          {'>'}
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </MuiThemeProvider>
  );
}

export default App;
